import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

const WA_NUMBER = '447459347738'; // +44 7459 347738, no spaces/plus for wa.me
const DEFAULT_MSG = encodeURIComponent("Hi Apptio team, I need help with my account.");

export default function WhatsAppHelpButton() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // Show ONLY on marketing/landing pages (not inside the authenticated app where it blocks touch on mobile)
  const landingPaths = ['/', '/landing', '/pricing', '/features', '/contact', '/terms', '/privacy', '/support', '/about'];
  if (!landingPaths.includes(pathname)) return null;
  const handleOpen = () => {
    // Open WhatsApp in new tab/app
    window.open(`https://wa.me/${WA_NUMBER}?text=${DEFAULT_MSG}`, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };
  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Need help on WhatsApp"
          data-testid="whatsapp-help-launcher"
          className="fixed bottom-4 left-4 z-[9999] flex items-center gap-2 px-3.5 py-2.5 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 bg-[#25D366] text-white"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.87c0 2.09.55 4.13 1.58 5.93L0 24l6.36-1.67a11.86 11.86 0 0 0 5.68 1.45h.01c6.54 0 11.86-5.32 11.86-11.87 0-3.17-1.23-6.15-3.39-8.43zM12.05 21.8h-.01a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.77.99 1-3.67-.23-.38a9.87 9.87 0 0 1-1.52-5.28c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.92-9.88 9.92zm5.43-7.41c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.3 1.26.48 1.69.62.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z"/>
          </svg>
          <span className="text-sm font-semibold">Need help?</span>
        </button>
      )}

      {/* Popover card */}
      {open && (
        <div
          className="fixed bottom-4 left-4 z-[9999] w-[300px] bg-white rounded-2xl shadow-2xl border border-[#E5E7E1] overflow-hidden"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="whatsapp-help-popover"
        >
          <div className="bg-[#128C7E] px-4 py-3 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.87c0 2.09.55 4.13 1.58 5.93L0 24l6.36-1.67a11.86 11.86 0 0 0 5.68 1.45h.01c6.54 0 11.86-5.32 11.86-11.87 0-3.17-1.23-6.15-3.39-8.43zM12.05 21.8h-.01a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.77.99 1-3.67-.23-.38a9.87 9.87 0 0 1-1.52-5.28c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.92-9.88 9.92zm5.43-7.41c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.3 1.26.48 1.69.62.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z"/>
              </svg>
              <div>
                <p className="text-sm font-bold">Apptio Support</p>
                <p className="text-[11px] opacity-90">Typically replies within a few hours</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="whatsapp-help-close"
              className="p-1 rounded hover:bg-white/20 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-4 py-4 bg-[#ECE5DD]">
            <div className="bg-white px-3 py-2 rounded-lg rounded-tl-none text-sm text-[#1A2421] shadow-sm max-w-[85%]">
              {'Hi there \u{1F44B}'}
              <br />
              How can we help you today?
            </div>
          </div>

          <div className="px-4 py-3 bg-white border-t border-[#E5E7E1]">
            <button
              onClick={handleOpen}
              data-testid="whatsapp-help-start"
              className="w-full bg-[#25D366] hover:bg-[#1ebe5a] active:bg-[#19a74e] text-white text-sm font-semibold py-2.5 rounded-full transition-colors flex items-center justify-center gap-2"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.87c0 2.09.55 4.13 1.58 5.93L0 24l6.36-1.67a11.86 11.86 0 0 0 5.68 1.45h.01c6.54 0 11.86-5.32 11.86-11.87 0-3.17-1.23-6.15-3.39-8.43zM12.05 21.8h-.01a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.77.99 1-3.67-.23-.38a9.87 9.87 0 0 1-1.52-5.28c0-5.45 4.44-9.88 9.9-9.88 2.64 0 5.12 1.03 6.99 2.9a9.82 9.82 0 0 1 2.89 6.99c0 5.45-4.44 9.92-9.88 9.92zm5.43-7.41c-.3-.15-1.76-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.5l-.57-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.3 1.26.48 1.69.62.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z"/>
              </svg>
              Start chat on WhatsApp
            </button>
            <p className="text-[10px] text-[#5C6656] text-center mt-2">
              +44 7459 347738
            </p>
          </div>
        </div>
      )}
    </>
  );
}
